import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import Sidebar from '../components/Sidebar';
import axios from 'axios';
import common from '../utils/common';
import { authStorage } from '../utils/authStorage';
import { fetchParticipants } from '../features/api/apiSlice';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import './ntc-branding.css';

const Active = () => {
  const dispatch = useAppDispatch();
  const { participants: reduxParticipants } = useAppSelector((state) => state.api);
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [commitments, setCommitments] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [addCommitment, setAddCommitment] = useState(false);
  const [loginData, setLoginData] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [projectModalMode, setProjectModalMode] = useState('view'); // 'view' for active projects
  const [editingProject, setEditingProject] = useState(null);
  const [isSavingProject, setIsSavingProject] = useState(false);
  const [isSubmittingCommitment, setIsSubmittingCommitment] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [originalProjects, setOriginalProjects] = useState([]);
  
  const [projectForm, setProjectForm] = useState({
    projectName: '',
    projectURL: '',
    whitepaperURL: '',
    allocation: '',
    allocation_currency: 'USDT',
    tokenomics: '',
    dealanalysis: '',
    fund_wallet: '',
    timer: '24',
    telegram: '',
    poc_email: '',
    whatsapp: '',
    referred: false,
    referred_by: 0
  });
  
  const [commitmentForm, setCommitmentForm] = useState({
    id: 0,
    participant_id: 0,
    proj_id: 0,
    allocation_amount: '',
    mode_payment: 'Binance',
    transaction_hash: '',
    wallet_address: '',
    comment: ''
  });

  // Custom Alert State
  const [customAlert, setCustomAlert] = useState({
    show: false,
    message: '',
    type: 'error'
  });

  const showAlert = (message, type = 'error') => {
    setCustomAlert({ show: true, message, type });
    setTimeout(() => {
      setCustomAlert({ show: false, message: '', type: 'error' });
    }, type === 'success' ? 4000 : 5000);
  };

  const hideAlert = () => {
    setCustomAlert({ show: false, message: '', type: 'error' });
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
    document.documentElement.classList.toggle('minimenu');
    localStorage.setItem('nexel-classic-dashboard-menu-mini-theme', !sidebarCollapsed ? 'menu-mini-theme' : '');
  };

  // Restore sidebar state from localStorage on component mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('nexel-classic-dashboard-menu-mini-theme');
    if (savedTheme === 'menu-mini-theme') {
      setSidebarCollapsed(true);
      document.documentElement.classList.add('minimenu');
    } else {
      setSidebarCollapsed(false);
      document.documentElement.classList.remove('minimenu');
    }
  }, []);

  useEffect(() => {
    const data = authStorage.getLoginData();
    if (!data) return;
    if (authStorage.getDirectLink() === '1') {
      window.location.href = '/';
      return;
    }
    setLoginData(data);
  }, []);

  // Fetch participants
  useEffect(() => {
    dispatch(fetchParticipants());
  }, [dispatch]);

  useEffect(() => {
    if (reduxParticipants) {
      setParticipants(reduxParticipants);
    }
  }, [reduxParticipants]);

  // Fetch active projects
  useEffect(() => {
    if (!loginData) return;
    
    const baseUrl = common.Data.API_URL;
    const url = window.location.href;
    const projectNameParam = url.split('?')[1];
    
    axios.get(`${baseUrl}/v1/get-projects/active-${loginData.id}-${loginData.role}`)
      .then((response) => {
        const projectsData = response.data || [];
        setProjects(projectsData);
        setOriginalProjects(projectsData);
        
        if (projectsData.length > 0) {
          let selected = projectsData[0];
          if (projectNameParam) {
            const found = projectsData.find(p => p.name.toLowerCase() === decodeURIComponent(projectNameParam).toLowerCase());
            if (found) selected = found;
          }
          populateCommitment(selected);
        }
      })
      .catch((error) => {
        console.error('Error fetching projects:', error);
        showAlert('Failed to fetch projects', 'error');
      });
  }, [loginData]);

  // Filter projects based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setProjects(originalProjects);
      return;
    }
    
    const filtered = originalProjects.filter(project => 
      project.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setProjects(filtered);
    
    // If selected project is filtered out, select first filtered project
    if (filtered.length > 0 && selectedProject && !filtered.find(p => p.id === selectedProject.id)) {
      const firstFiltered = filtered[0];
      populateCommitment(firstFiltered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, originalProjects]);

  // Update timer
  useEffect(() => {
    if (!selectedProject || !selectedProject.active_on) return;

    const updateTimer = () => {
      const projectActivationTime = new Date(selectedProject.active_on);
      const now = new Date();
      const timeDiff = now - projectActivationTime;
      const timerHours = 24;
      const remaining = Math.ceil((timerHours * 3600 * 1000 - timeDiff) / 1000);
      setRemainingTime(remaining > 0 ? remaining : 0);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    
    return () => clearInterval(interval);
  }, [selectedProject]);

  const populateCommitment = (project) => {
    if (!project) return;
    
    const baseUrl = common.Data.API_URL;
    axios.get(`${baseUrl}/v1/get-commitment/${project.id}`)
      .then((response) => {
        setCommitments(response.data || []);
        setSelectedProject(project);
        setAddCommitment(false);
      })
      .catch((error) => {
        console.error('Error fetching commitments:', error);
        showAlert('Failed to fetch commitment details', 'error');
      });
  };

  const openCommitment = (commitment) => {
    setCommitmentForm({
      id: commitment.id || 0,
      participant_id: commitment.participant_id || 0,
      proj_id: selectedProject.id,
      allocation_amount: commitment.allocation_amount || '',
      mode_payment: commitment.mode_payment || 'Binance',
      transaction_hash: commitment.transaction_hash || '',
      wallet_address: commitment.wallet_address || '',
      comment: commitment.comment || ''
    });
    setAddCommitment(true);
  };

  const goBack = () => {
    setAddCommitment(false);
    setCommitmentForm({
      id: 0,
      participant_id: 0,
      proj_id: 0,
      allocation_amount: '',
      mode_payment: 'Binance',
      transaction_hash: '',
      wallet_address: '',
      comment: ''
    });
  };

  const saveCommitment = () => {
    if (!commitmentForm.transaction_hash || commitmentForm.transaction_hash === '') {
      showAlert('Please fill all mandatory fields', 'error');
      return;
    }

    let validTxUrl = true;
    if (commitmentForm.mode_payment === 'Etherscan') {
      if (!commitmentForm.transaction_hash.includes('https://etherscan.io/tx/')) {
        validTxUrl = false;
      }
    }

    if (!validTxUrl) {
      showAlert('Please provide a valid etherscan url. It should be in the format -> https://etherscan.io/tx/.....', 'error');
      return;
    }

    const baseUrl = common.Data.API_URL;
    const formData = {
      ...commitmentForm,
      proj_id: selectedProject.id,
      mode_payment: commitmentForm.mode_payment || 'Binance',
      comment: commitmentForm.comment || ''
    };

    setIsSubmittingCommitment(true);
    axios.post(`${baseUrl}/v1/save-commitment`, formData)
      .then(() => {
        showAlert('Commitment saved successfully', 'success');
        window.location.href = `/active?${selectedProject.name}`;
      })
      .catch((error) => {
        console.error('Error saving commitment:', error);
        showAlert('Failed to save commitment', 'error');
      })
      .finally(() => {
        setIsSubmittingCommitment(false);
      });
  };

  const approveTx = (verified, id) => {
    const baseUrl = common.Data.API_URL;
    axios.get(`${baseUrl}/v1/trasaction-verification/${parseInt(id)}/${parseInt(verified)}`)
      .then(() => {
        showAlert(verified === 1 ? 'Transaction approved' : 'Transaction rejected', 'success');
        populateCommitment(selectedProject);
      })
      .catch((error) => {
        console.error('Error verifying transaction:', error);
        showAlert('Operation failed', 'error');
      });
  };

  const sendToInvested = () => {
    if (!selectedProject) return;
    
    const baseUrl = common.Data.API_URL;
    axios.get(`${baseUrl}/v1/send-invested/${selectedProject.id}/${loginData.id}`)
      .then(() => {
        showAlert('Project moved to invested successfully', 'success');
        setTimeout(() => {
          window.location.href = `/unreleased?${selectedProject.name}`;
        }, 1500);
      })
      .catch((error) => {
        console.error('Error sending to invested:', error);
        showAlert('Failed to move project', 'error');
      });
  };

  // Helper function to process HTML and make links open in new tabs
  const processLinksInHTML = (html) => {
    if (!html) return html;
    // Use a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const links = tempDiv.querySelectorAll('a');
    links.forEach(link => {
      if (!link.getAttribute('target')) {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      }
    });
    return tempDiv.innerHTML;
  };

  const handleViewProject = async (project) => {
    if (!project) return;
    
    setProjectModalMode('view');
    setEditingProject(project);
    setShowAddProjectModal(true);
    
    // Process tokenomics and dealanalysis HTML to ensure links open in new tabs
    const processedTokenomics = processLinksInHTML(project.tokenomics || '');
    const processedDealAnalysis = processLinksInHTML(project.deal_analysis || '');
    
    // Populate form with project data
    setProjectForm({
      projectName: project.name || '',
      projectURL: project.url || '',
      whitepaperURL: project.whitepaper_url || '',
      allocation: project.allocation_available || '',
      allocation_currency: project.allocation_currency || 'USDT',
      tokenomics: processedTokenomics,
      dealanalysis: processedDealAnalysis,
      fund_wallet: project.fund_wallet_address || '',
      timer: project.timer || '24',
      telegram: project.telegram || '',
      poc_email: project.email || '',
      whatsapp: project.whatsapp || '',
      referred: project.status === 'referred',
      referred_by: project.referred_by || loginData?.id || 0
    });
  };

  const handleCloseAddProjectModal = () => {
    setShowAddProjectModal(false);
    setProjectModalMode('view');
    setEditingProject(null);
    setProjectForm({
      projectName: '',
      projectURL: '',
      whitepaperURL: '',
      allocation: '',
      allocation_currency: 'USDT',
      tokenomics: '',
      dealanalysis: '',
      fund_wallet: '',
      timer: '24',
      telegram: '',
      poc_email: '',
      whatsapp: '',
      referred: false,
      referred_by: loginData?.id || 0
    });
  };

  const formatTime = (seconds) => {
    if (seconds <= 0) return '00:00:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Calculate stats
  const calculateStats = () => {
    if (!selectedProject) {
      return {
        totalAllocation: 0,
        participants: 0,
        totalCommitment: 0,
        allocationAvailable: 0
      };
    }
    
    const totalAllocation = selectedProject.allocation_available ? parseInt(selectedProject.allocation_available) : 0;
    let totalCommitment = 0;
    
    commitments.forEach(commit => {
      totalCommitment += parseInt(commit.allocation_amount || 0);
    });
    
    const allocationAvailable = totalAllocation - totalCommitment;
    const participants = commitments.length;
    
    return {
      totalAllocation,
      participants,
      totalCommitment,
      allocationAvailable
    };
  };

  const stats = calculateStats();
  const isSpecialProject = selectedProject?.is_special && 
                          !(selectedProject.special_participants && selectedProject.special_participants.split(',').includes(loginData?.id + '')) && 
                          loginData?.role != 1;

  return (
    <div className="nxl-wrapper">
      {/* Custom Alert */}
      {customAlert.show && (
        <div 
          className="position-fixed"
          style={{
            top: '24px',
            right: '24px',
            zIndex: 9999,
            minWidth: '380px',
            maxWidth: '450px',
            animation: 'slideInRight 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
          }}
        >
          <div 
            className="card shadow-lg border-0"
            style={{
              borderRadius: '16px',
              overflow: 'hidden',
              border: `2px solid ${
                customAlert.type === 'error' ? '#dc3545' : 
                customAlert.type === 'success' ? '#28a745' : 
                customAlert.type === 'warning' ? '#ffc107' : 
                '#17a2b8'
              }`
            }}
          >
            <div 
              className={`card-body p-3 d-flex align-items-center position-relative`}
              style={{
                backgroundColor: '#ffffff',
                borderLeft: `5px solid ${
                  customAlert.type === 'error' ? '#dc3545' : 
                  customAlert.type === 'success' ? '#28a745' : 
                  customAlert.type === 'warning' ? '#ffc107' : 
                  '#17a2b8'
                }`
              }}
            >
              <div 
                className={`d-flex align-items-center justify-content-center me-3`}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: customAlert.type === 'error' ? 'rgba(220, 53, 69, 0.15)' : 
                                   customAlert.type === 'success' ? 'rgba(40, 167, 69, 0.15)' : 
                                   customAlert.type === 'warning' ? 'rgba(255, 193, 7, 0.15)' : 
                                   'rgba(23, 162, 184, 0.15)',
                  flexShrink: 0,
                  boxShadow: `0 2px 8px rgba(0,0,0,0.08)`
                }}
              >
                <i 
                  className={`feather-${
                    customAlert.type === 'error' ? 'alert-circle' : 
                    customAlert.type === 'success' ? 'check-circle' : 
                    customAlert.type === 'warning' ? 'alert-triangle' : 
                    'info'
                  }`}
                  style={{ 
                    fontSize: '24px', 
                    color: customAlert.type === 'error' ? '#dc3545' : 
                           customAlert.type === 'success' ? '#28a745' : 
                           customAlert.type === 'warning' ? '#ffc107' : 
                           '#17a2b8'
                  }}
                ></i>
              </div>
              <div className="flex-grow-1">
                <h6 
                  className={`mb-1 fw-bold`}
                  style={{ fontSize: '16px', 
                           color: customAlert.type === 'error' ? '#dc3545' : 
                                  customAlert.type === 'success' ? '#28a745' : 
                                  customAlert.type === 'warning' ? '#ffc107' : 
                                  '#17a2b8'
                         }}
                >
                  {customAlert.type === 'error' ? 'Error' : 
                   customAlert.type === 'success' ? 'Success' : 
                   customAlert.type === 'warning' ? 'Warning' : 
                   'Information'}
                </h6>
                <p className="mb-0 text-dark" style={{ fontSize: '14px', lineHeight: '1.4' }}>
                  {customAlert.message}
                </p>
              </div>
              <button
                type="button"
                className="btn-close ms-2"
                onClick={hideAlert}
                aria-label="Close"
                style={{
                  opacity: 0.8,
                  transition: 'all 0.2s ease',
                  transform: 'scale(1)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              ></button>
            </div>
            <div 
              className="progress" 
              style={{ height: '4px', borderRadius: '0', backgroundColor: 'rgba(0,0,0,0.05)' }}
            >
              <div 
                className={`progress-bar ${
                  customAlert.type === 'error' ? 'bg-danger' : 
                  customAlert.type === 'success' ? 'bg-success' : 
                  customAlert.type === 'warning' ? 'bg-warning' : 
                  'bg-info'
                }`}
                role="progressbar" 
                style={{ width: '100%', animation: `progressBar ${customAlert.type === 'success' ? 4 : 5}s linear forwards` }} 
                aria-valuenow="100" 
                aria-valuemin="0" 
                aria-valuemax="100"
              ></div>
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%) scale(0.9);
            opacity: 0;
          }
          to {
            transform: translateX(0) scale(1);
            opacity: 1;
          }
        }
        @keyframes progressBar {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>

      <Sidebar 
        sidebarCollapsed={sidebarCollapsed} 
        setSidebarCollapsed={setSidebarCollapsed} 
        toggleSidebar={toggleSidebar}
      />

      {/* Main Content */}
      <main className="nxl-container" style={{ paddingTop: '0' }}>
        <div className="nxl-content" style={{ paddingTop: '0' }}>
          {/* Page Header */}
          <div className="page-header">
            <div className="page-header-left d-flex align-items-center">
              {/* Sidebar Toggle Button */}
              <div className="nxl-navigation-toggle me-3">
                <a href="javascript:void(0);" id="menu-mini-button" onClick={toggleSidebar} style={{ display: sidebarCollapsed ? 'none' : 'block' }}>
                  <i className="feather-align-left"></i>
                </a>
                <a href="javascript:void(0);" id="menu-expend-button" onClick={toggleSidebar} style={{ display: sidebarCollapsed ? 'block' : 'none' }}>
                  <i className="feather-arrow-right"></i>
                </a>
              </div>
              <div className="page-header-title">
                <h5 className="m-b-10">Active Projects</h5>
              </div>
              <ul className="breadcrumb">
                <li className="breadcrumb-item"><a href="/investor-dashboard">Home</a></li>
                <li className="breadcrumb-item">Active Projects</li>
              </ul>
            </div>
            <div className="page-header-right ms-auto d-flex align-items-center gap-3">
              {/* Search Bar */}
              <div className="position-relative" style={{ width: '200px' }}>
                <i className="feather-search position-absolute" style={{ left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: '14px', pointerEvents: 'none' }}></i>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search project"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    paddingLeft: '32px',
                    paddingRight: searchTerm ? '28px' : '12px',
                    fontSize: '12px',
                    borderColor: '#dee2e6',
                    borderRadius: '6px',
                    height: '32px'
                  }}
                />
                {searchTerm && (
                  <button
                    type="button"
                    className="btn-close position-absolute"
                    onClick={() => setSearchTerm('')}
                    style={{
                      right: '6px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '10px',
                      opacity: 0.6,
                      width: '16px',
                      height: '16px'
                    }}
                    aria-label="Clear search"
                  ></button>
                )}
              </div>
              {loginData && (
                <div className="d-flex align-items-center gap-2">
                  <i className="feather-user text-primary"></i>
                  <span className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                    Welcome, {(() => {
                      if (participants.length > 0 && loginData.id) {
                        const userParticipant = participants.find(p => p.participant_id == loginData.id || p.id == loginData.id);
                        return userParticipant?.name || loginData.name || 'User';
                      }
                      return loginData.name || 'User';
                    })()}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="container-fluid py-4">

          {/* Project Navigation Tabs */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body p-0">
              <ul className="nav nav-tabs border-0" style={{ 
                padding: '0.5rem 1rem',
                borderBottom: '1px solid #dee2e6',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.25rem'
              }}>
                {projects.map(project => {
                  const isVisible = !project.is_special || 
                                   (project.special_participants && project.special_participants.split(',').includes(loginData?.id + '')) || 
                                   loginData?.role == 1;
                  if (!isVisible) return null;
                  
                  const isActive = selectedProject?.id === project.id;
                  
                  return (
                    <li key={project.id} className="nav-item">
                      <button
                        className={`nav-link ${isActive ? 'active' : ''}`}
                        onClick={() => populateCommitment(project)}
                        style={{ 
                          border: 'none',
                          background: isActive ? 'rgba(13, 110, 253, 0.1)' : 'none',
                          cursor: 'pointer',
                          padding: '0.625rem 1.25rem',
                          color: isActive ? '#0d6efd' : '#495057',
                          fontWeight: '600',
                          fontSize: '13px',
                          borderRadius: '0',
                          borderBottom: isActive ? '3px solid #0d6efd' : '3px solid transparent',
                          transition: 'all 0.3s ease',
                          position: 'relative'
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.color = '#0d6efd';
                            e.currentTarget.style.backgroundColor = 'rgba(13, 110, 253, 0.05)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.currentTarget.style.color = '#495057';
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {project.name}
                        {(project.reco_done || project.recommendation_done) && (
                          <span 
                            style={{
                              position: 'absolute',
                              top: '4px',
                              right: '4px',
                              width: '8px',
                              height: '8px',
                              backgroundColor: '#28a745',
                              borderRadius: '50%',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                            }}
                          ></span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Timer Banner */}
          {selectedProject && (
            <div 
              className="mb-4 border rounded d-flex align-items-center gap-2"
              style={{
                backgroundColor: remainingTime > 0 ? '#ffffff' : '#fff8f0',
                borderColor: remainingTime > 0 ? '#dee2e6' : '#ffc107',
                borderWidth: remainingTime > 0 ? '1px' : '1.5px',
                borderLeftWidth: remainingTime > 0 ? '1px' : '4px',
                borderLeftColor: remainingTime > 0 ? '#dee2e6' : '#ffc107',
                padding: '0.75rem 1rem'
              }}
            >
              <i 
                className={`feather-${remainingTime > 0 ? 'clock' : 'alert-triangle'}`} 
                style={{ 
                  fontSize: '16px',
                  color: remainingTime > 0 ? '#0d6efd' : '#ffc107',
                  flexShrink: 0
                }}
              ></i>
              <span style={{ fontSize: '13px', color: remainingTime > 0 ? '#6c757d' : '#856404', fontWeight: '500' }}>
                {remainingTime > 0 ? 'Allocation Payment Deadline:' : 'Payment Submission Closed:'}
              </span>
              {remainingTime > 0 ? (
                <>
                  <span style={{ fontFamily: 'monospace', letterSpacing: '0.5px', color: '#0d6efd', fontWeight: '600', fontSize: '14px' }}>
                    {formatTime(remainingTime)}
                  </span>
                  <span className="text-muted" style={{ fontSize: '12px' }}>remaining</span>
                </>
              ) : (
                <span style={{ fontSize: '13px', color: '#856404', fontWeight: '600' }}>Time expired</span>
              )}
              <span className="ms-auto text-muted" style={{ fontSize: '12px' }}>
                {remainingTime > 0 ? 'Submit payment within 24 hours of project upload' : '24-hour window ended'}
              </span>
            </div>
          )}

          {/* Stats Cards */}
          {selectedProject && !isSpecialProject && (
            <div className="row mb-4">
              <div className="col-sm-6 col-lg-3 mb-3">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center gap-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: '#e3f2fd' }}>
                        <i className="feather-dollar-sign text-primary" style={{ fontSize: '20px' }}></i>
                      </div>
                      <div>
                        <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500' }}>Total Allocation</div>
                        <div className="fw-bold text-dark" style={{ fontSize: '18px' }}>
                          {stats.totalAllocation.toLocaleString()} {selectedProject.allocation_currency || 'USDT'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3 mb-3">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center gap-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: '#fff3e0' }}>
                        <i className="feather-users text-warning" style={{ fontSize: '20px' }}></i>
                      </div>
                      <div>
                        <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500' }}>Participants</div>
                        <div className="fw-bold text-warning" style={{ fontSize: '18px' }}>
                          {stats.participants}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3 mb-3">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center gap-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: '#ffebee' }}>
                        <i className="feather-trending-up text-danger" style={{ fontSize: '20px' }}></i>
                      </div>
                      <div>
                        <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500' }}>Allocation Used</div>
                        <div className="fw-bold text-danger" style={{ fontSize: '18px' }}>
                          {stats.totalCommitment.toLocaleString()} {selectedProject.allocation_currency || 'USDT'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3 mb-3">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center gap-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: '#e8f5e9' }}>
                        <i className="feather-check-circle text-success" style={{ fontSize: '20px' }}></i>
                      </div>
                      <div>
                        <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500' }}>Allocation Available</div>
                        <div className="fw-bold text-success" style={{ fontSize: '18px' }}>
                          {stats.allocationAvailable.toLocaleString()} {selectedProject.allocation_currency || 'USDT'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons & Fund Wallet */}
          {selectedProject && selectedProject.name && (
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                  {/* Action Buttons - Left Side */}
                  <div className="d-flex gap-2 flex-wrap">
                    {loginData?.role == 1 && (
                      <button 
                        type="button" 
                        className="btn btn-warning btn-sm"
                        onClick={() => handleViewProject(selectedProject)}
                        disabled={isSpecialProject}
                      >
                        <i className="feather-eye me-1"></i>View {selectedProject.name}
                      </button>
                    )}
                    {loginData?.role == 1 && remainingTime <= 0 && (
                      <button 
                        type="button" 
                        className="btn btn-warning btn-sm"
                        onClick={sendToInvested}
                      >
                        <i className="feather-arrow-right me-1"></i>Send to Invested
                      </button>
                    )}
                  </div>
                  
                  {/* Fund Wallet Address - Right Side */}
                  {remainingTime > 0 && selectedProject.fund_wallet_address && (
                    <div className="d-flex align-items-center gap-2" style={{ flex: 1, minWidth: '250px', justifyContent: 'flex-end' }}>
                      <div>
                        <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500' }}>Send allocation to (ERC20 / BSC):</div>
                        <div className="d-flex align-items-center gap-2">
                          <div className="fw-bold text-danger" style={{ fontSize: '14px', wordBreak: 'break-all' }}>
                            {selectedProject.fund_wallet_address}
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm p-1"
                            onClick={() => {
                              navigator.clipboard.writeText(selectedProject.fund_wallet_address);
                              showAlert('Wallet address copied to clipboard', 'success');
                            }}
                            title="Copy to clipboard"
                            style={{ minWidth: 'auto', height: 'auto', lineHeight: '1' }}
                          >
                            <i className="feather-copy" style={{ fontSize: '14px' }}></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Main Content Card */}
          <div className="card shadow-sm border-0">
            <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
              <h6 className="mb-0 fw-bold">Allocation Details</h6>
            </div>
            <div className="card-body">
              {!addCommitment ? (
                <div className="table-responsive" style={{ overflowX: 'auto', maxWidth: '100%' }}>
                  <table className="table table-hover mb-0" style={{ width: '100%', tableLayout: 'fixed' }}>
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '5%' }}>S. No.</th>
                        <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '12%' }}>Name</th>
                        <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '8%' }}>Currency</th>
                        <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '10%' }}>Amount</th>
                        <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '10%' }}>Alloc Amt</th>
                        <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '8%' }}>Mode</th>
                        <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '20%' }}>Transaction Hash</th>
                        <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '15%' }}>Comment</th>
                        <th className="border-0 px-2 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '12%' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commitments.map((commit, index) => {
                        if (commit.participant_id !== loginData?.id && loginData?.role != 1) return null;
                        
                        return (
                          <tr key={commit.id || index} className="border-bottom">
                            <td className="px-2 py-2 text-center">
                              <span className="fw-semibold text-dark fs-12">{index + 1}</span>
                            </td>
                            <td className="px-2 py-2 text-center">
                              <span className="fw-medium text-dark fs-12" style={{ wordBreak: 'break-word' }}>{commit.participant_name}</span>
                            </td>
                            <td className="px-2 py-2 text-center">
                              <span className="fw-semibold text-dark fs-12">{selectedProject?.allocation_currency || 'USDT'}</span>
                            </td>
                            <td className="px-2 py-2 text-center">
                              <span className="fw-semibold text-dark fs-12">{commit.amount || 0}</span>
                            </td>
                            <td className="px-2 py-2 text-center">
                              <span className="fw-semibold text-dark fs-12">{commit.allocation_amount || 0}</span>
                            </td>
                            <td className="px-2 py-2 text-center">
                              <span className="fw-medium text-dark fs-12">{commit.mode_payment || '-'}</span>
                            </td>
                            <td className="px-2 py-2 text-center">
                              {commit.transaction_hash ? (
                                commit.transaction_hash.includes('https') ? (
                                  <a 
                                    href={commit.transaction_hash} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="text-primary"
                                    style={{ fontSize: '11px', wordBreak: 'break-all', display: 'block' }}
                                    title={commit.transaction_hash}
                                  >
                                    {commit.transaction_hash.length > 25 ? `${commit.transaction_hash.substring(0, 25)}...` : commit.transaction_hash}
                                  </a>
                                ) : (
                                  <span 
                                    className="text-primary"
                                    style={{ fontSize: '11px', cursor: 'pointer', wordBreak: 'break-all', display: 'block' }}
                                    onClick={() => {
                                      navigator.clipboard.writeText(commit.transaction_hash);
                                      showAlert('Transaction hash copied', 'success');
                                    }}
                                    title={commit.transaction_hash}
                                  >
                                    {commit.transaction_hash.length > 25 ? `${commit.transaction_hash.substring(0, 25)}...` : commit.transaction_hash}
                                  </span>
                                )
                              ) : (
                                <span className="text-muted fs-12">-</span>
                              )}
                            </td>
                            <td className="px-2 py-2 text-center">
                              <span className="fw-medium text-dark fs-12" style={{ wordBreak: 'break-word' }} title={commit.comment || ''}>{commit.comment ? (commit.comment.length > 20 ? `${commit.comment.substring(0, 20)}...` : commit.comment) : '-'}</span>
                            </td>
                            <td className="px-2 py-2 text-center">
                              {commit.is_verified === null ? (
                                <div className="d-flex gap-1 justify-content-center flex-wrap">
                                  {((commit.participant_id === loginData?.id && remainingTime > 0) || loginData?.id == 1) && (
                                    <button
                                      type="button"
                                      className="btn btn-warning btn-sm"
                                      onClick={() => openCommitment(commit)}
                                      title="Edit Commitment"
                                      style={{ padding: '2px 6px', fontSize: '11px' }}
                                    >
                                      <i className="feather-edit" style={{ fontSize: '12px' }}></i>
                                    </button>
                                  )}
                                  {commit.transaction_hash && commit.transaction_hash !== '' && loginData?.id == 1 && (
                                    <>
                                      <button
                                        type="button"
                                        className="btn btn-success btn-sm"
                                        onClick={() => approveTx(1, commit.id)}
                                        title="Approve"
                                        style={{ padding: '2px 6px', fontSize: '11px' }}
                                      >
                                        <i className="feather-check" style={{ fontSize: '12px' }}></i>
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-danger btn-sm"
                                        onClick={() => approveTx(0, commit.id)}
                                        title="Reject"
                                        style={{ padding: '2px 6px', fontSize: '11px' }}
                                      >
                                        <i className="feather-x" style={{ fontSize: '12px' }}></i>
                                      </button>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <span className={`badge ${commit.is_verified == 1 ? 'bg-success' : 'bg-danger'}`} style={{ fontSize: '10px' }}>
                                  {commit.is_verified == 1 ? 'Verified' : 'Rejected'}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {commitments.length === 0 && (
                        <tr>
                          <td colSpan="9" className="text-center py-4 text-muted">
                            <i className="feather-inbox fs-1 mb-2 d-block"></i>
                            No commitment details found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div>
                  <h6 className="fw-bold mb-3">
                    <i className="feather-edit me-2 text-primary"></i>Allocation Fulfillment Form
                  </h6>
                  <form>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold mb-2 text-dark fs-13">
                          <i className="feather-user me-2 text-primary fs-12"></i>
                          Participant Name <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-select"
                          value={commitmentForm.participant_id}
                          disabled
                          style={{ borderColor: '#dee2e6', fontSize: '13px' }}
                        >
                          {participants.map(p => (
                            <option key={p.participant_id} value={p.participant_id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold mb-2 text-dark fs-13">
                          <i className="feather-dollar-sign me-2 text-primary fs-12"></i>
                          Allocation Currency <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-select"
                          value={selectedProject?.allocation_currency || 'USDT'}
                          disabled
                          style={{ borderColor: '#dee2e6', fontSize: '13px' }}
                        >
                          <option value="USDT">USDT</option>
                          <option value="USDC">USDC</option>
                          <option value="ETH">Ethereum</option>
                          <option value="DAI">DAI</option>
                        </select>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold mb-2 text-dark fs-13">
                          <i className="feather-trending-up me-2 text-primary fs-12"></i>
                          Amount <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="0.00"
                          value={commitmentForm.allocation_amount}
                          disabled
                          style={{ borderColor: '#dee2e6', fontSize: '13px' }}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold mb-2 text-dark fs-13">
                          <i className="feather-send me-2 text-primary fs-12"></i>
                          Mode of Transfer <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-select"
                          value={commitmentForm.mode_payment}
                          onChange={(e) => setCommitmentForm({ ...commitmentForm, mode_payment: e.target.value })}
                          style={{ borderColor: '#dee2e6', fontSize: '13px' }}
                        >
                          <option value="Binance">Binance</option>
                          <option value="Etherscan">Etherscan</option>
                          <option value="Direct transfer">Direct Transfer</option>
                        </select>
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold mb-2 text-dark fs-13">
                          <i className="feather-link me-2 text-primary fs-12"></i>
                          Transaction Hash / Etherscan URL <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter Etherscan url or if sent via binance provide the tx id"
                          value={commitmentForm.transaction_hash}
                          onChange={(e) => setCommitmentForm({ ...commitmentForm, transaction_hash: e.target.value })}
                          style={{ borderColor: '#dee2e6', fontSize: '13px' }}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label fw-semibold mb-2 text-dark fs-13">
                          <i className="feather-wallet me-2 text-primary fs-12"></i>
                          Wallet Address
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter wallet address"
                          value={commitmentForm.wallet_address !== 'undefined' ? commitmentForm.wallet_address : ''}
                          onChange={(e) => setCommitmentForm({ ...commitmentForm, wallet_address: e.target.value })}
                          style={{ borderColor: '#dee2e6', fontSize: '13px' }}
                        />
                      </div>
                      <div className="col-12 mb-3">
                        <label className="form-label fw-semibold mb-2 text-dark fs-13">
                          <i className="feather-message-circle me-2 text-primary fs-12"></i>
                          Comment
                        </label>
                        <textarea
                          className="form-control"
                          rows="3"
                          placeholder="Comment (if any)"
                          value={commitmentForm.comment}
                          onChange={(e) => setCommitmentForm({ ...commitmentForm, comment: e.target.value })}
                          style={{ borderColor: '#dee2e6', fontSize: '13px', resize: 'none' }}
                        />
                      </div>
                    </div>
                    <div className="d-flex gap-2 justify-content-end mt-3 pt-3 border-top">
                      <button
                        type="button"
                        className="btn btn-light btn-sm"
                        onClick={goBack}
                        style={{
                          borderRadius: '8px',
                          padding: '8px 20px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}
                      >
                        <i className="feather-x me-1"></i>Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={saveCommitment}
                        disabled={isSubmittingCommitment}
                        style={{
                          borderRadius: '8px',
                          padding: '8px 24px',
                          fontSize: '12px',
                          fontWeight: '600',
                          opacity: isSubmittingCommitment ? 0.6 : 1,
                          cursor: isSubmittingCommitment ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {isSubmittingCommitment ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <i className="feather-save me-1"></i>Submit
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </main>

      {/* Right Sidebar Modal for View Project */}
      {showAddProjectModal && (
        <>
          {/* Backdrop */}
          <div
            className="position-fixed"
            style={{
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1040,
              animation: 'fadeIn 0.3s ease'
            }}
            onClick={handleCloseAddProjectModal}
          />
          
          {/* Sidebar Modal */}
          <div
            className="position-fixed bg-white shadow-lg"
            style={{
              top: 0,
              right: 0,
              bottom: 0,
              width: '600px',
              maxWidth: '90vw',
              zIndex: 1050,
              overflowY: 'auto',
              animation: 'slideInRight 0.3s ease',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Modal Header */}
            <div
              className="border-bottom bg-white"
              style={{
                padding: '1.5rem',
                position: 'sticky',
                top: 0,
                zIndex: 10,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 fw-bold">
                  <i className="feather-eye me-2 text-primary"></i>
                  View Project
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseAddProjectModal}
                  aria-label="Close"
                ></button>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '1.5rem', flex: 1 }}>
              <form onSubmit={(e) => { e.preventDefault(); }}>
                <div className="row">
                  {/* Project Name */}
                  <div className="col-12 mb-3">
                    <label className="form-label fw-semibold mb-2 text-dark fs-13">
                      <i className="feather-file-text me-2 text-primary fs-12"></i>
                      Project Name
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={projectForm.projectName}
                      disabled
                      style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: '#f8f9fa' }}
                    />
                  </div>

                  {/* Project URL */}
                  <div className="col-12 mb-3">
                    <label className="form-label fw-semibold mb-2 text-dark fs-13">
                      <i className="feather-link me-2 text-primary fs-12"></i>
                      Project URL
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      value={projectForm.projectURL}
                      disabled
                      style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: '#f8f9fa' }}
                    />
                  </div>

                  {/* Whitepaper URL */}
                  <div className="col-12 mb-3">
                    <label className="form-label fw-semibold mb-2 text-dark fs-13">
                      <i className="feather-book me-2 text-primary fs-12"></i>
                      Whitepaper URL
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      value={projectForm.whitepaperURL}
                      disabled
                      style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: '#f8f9fa' }}
                    />
                  </div>

                  {/* Allocation Currency */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold mb-2 text-dark fs-13">
                      <i className="feather-dollar-sign me-2 text-primary fs-12"></i>
                      Allocation Currency
                    </label>
                    <select
                      className="form-select"
                      value={projectForm.allocation_currency}
                      disabled
                      style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: '#f8f9fa' }}
                    >
                      <option value="USDT">USDT</option>
                      <option value="USDC">USDC</option>
                      <option value="ETH">Ethereum</option>
                      <option value="DAI">DAI</option>
                    </select>
                  </div>

                  {/* Allocation Available */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold mb-2 text-dark fs-13">
                      <i className="feather-trending-up me-2 text-primary fs-12"></i>
                      Allocation Available
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={projectForm.allocation}
                      disabled
                      style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: '#f8f9fa' }}
                    />
                  </div>

                  {/* Fund Wallet */}
                  <div className="col-12 mb-3">
                    <label className="form-label fw-semibold mb-2 text-dark fs-13">
                      <i className="feather-wallet me-2 text-primary fs-12"></i>
                      Fund Wallet Address
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={projectForm.fund_wallet}
                      disabled
                      style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: '#f8f9fa' }}
                    />
                  </div>

                  {/* Timer */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold mb-2 text-dark fs-13">
                      <i className="feather-clock me-2 text-primary fs-12"></i>
                      Timer (hours)
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={projectForm.timer}
                      disabled
                      style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: '#f8f9fa' }}
                    />
                  </div>

                  {/* Telegram */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold mb-2 text-dark fs-13">
                      <i className="feather-send me-2 text-primary fs-12"></i>
                      Telegram
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={projectForm.telegram}
                      disabled
                      style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: '#f8f9fa' }}
                    />
                  </div>

                  {/* POC Email */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold mb-2 text-dark fs-13">
                      <i className="feather-mail me-2 text-primary fs-12"></i>
                      POC Email
                    </label>
                    <input
                      type="email"
                      className="form-control"
                      value={projectForm.poc_email}
                      disabled
                      style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: '#f8f9fa' }}
                    />
                  </div>

                  {/* WhatsApp */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label fw-semibold mb-2 text-dark fs-13">
                      <i className="feather-phone me-2 text-primary fs-12"></i>
                      WhatsApp No.
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={projectForm.whatsapp}
                      disabled
                      style={{ borderColor: '#dee2e6', fontSize: '13px', backgroundColor: '#f8f9fa' }}
                    />
                  </div>

                  {/* Tokenomics/Deal Details */}
                  <div className="col-12 mb-3">
                    <label className="form-label fw-semibold mb-2 text-dark fs-13">
                      <i className="feather-file-text me-2 text-primary fs-12"></i>
                      Tokenomics/Deal Details
                    </label>
                    <div 
                      className="form-control ck-content-view"
                      style={{ 
                        borderColor: '#dee2e6', 
                        fontSize: '13px', 
                        backgroundColor: '#f8f9fa',
                        minHeight: '200px',
                        padding: '12px',
                        overflow: 'auto'
                      }}
                      dangerouslySetInnerHTML={{ __html: projectForm.tokenomics || '<p class="text-muted">No content</p>' }}
                    />
                  </div>

                  {/* Deal Analysis */}
                  {!projectForm.referred && (
                    <div className="col-12 mb-3">
                      <label className="form-label fw-semibold mb-2 text-dark fs-13">
                        <i className="feather-bar-chart-2 me-2 text-primary fs-12"></i>
                        Deal Analysis
                      </label>
                      <div 
                        className="form-control ck-content-view"
                        style={{ 
                          borderColor: '#dee2e6', 
                          fontSize: '13px', 
                          backgroundColor: '#f8f9fa',
                          minHeight: '200px',
                          padding: '12px',
                          overflow: 'auto'
                        }}
                        dangerouslySetInnerHTML={{ __html: projectForm.dealanalysis || '<p class="text-muted">No content</p>' }}
                      />
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Modal Footer */}
            <div
              className="border-top bg-white"
              style={{
                padding: '1.5rem',
                position: 'sticky',
                bottom: 0,
                zIndex: 10,
                boxShadow: '0 -2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <div className="d-flex gap-2 justify-content-end">
                <button
                  type="button"
                  className="btn btn-light btn-sm"
                  onClick={handleCloseAddProjectModal}
                  style={{
                    borderRadius: '8px',
                    padding: '8px 20px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}
                >
                  <i className="feather-x me-1"></i>Close
                </button>
              </div>
            </div>
          </div>

          {/* Animation Styles */}
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes slideInRight {
              from { transform: translateX(100%); }
              to { transform: translateX(0); }
            }
            .ck-content-view h1,
            .ck-content-view h2,
            .ck-content-view h3,
            .ck-content-view h4,
            .ck-content-view h5,
            .ck-content-view h6 {
              margin-top: 0.75rem;
              margin-bottom: 0.5rem;
              font-weight: 600;
              line-height: 1.2;
            }
            .ck-content-view h1 { font-size: 1.5rem; }
            .ck-content-view h2 { font-size: 1.3rem; }
            .ck-content-view h3 { font-size: 1.1rem; }
            .ck-content-view p {
              margin-bottom: 0.75rem;
              line-height: 1.6;
            }
            .ck-content-view ul,
            .ck-content-view ol {
              margin-bottom: 0.75rem;
              padding-left: 1.5rem;
            }
            .ck-content-view li {
              margin-bottom: 0.25rem;
            }
            .ck-content-view table {
              width: 100%;
              border-collapse: collapse;
              margin: 0.75rem 0;
            }
            .ck-content-view table td,
            .ck-content-view table th {
              border: 1px solid #dee2e6;
              padding: 0.5rem;
              text-align: left;
            }
            .ck-content-view table th {
              background-color: #e9ecef;
              font-weight: 600;
            }
            .ck-content-view blockquote {
              border-left: 4px solid #0d6efd;
              padding-left: 1rem;
              margin: 0.75rem 0;
              font-style: italic;
              color: #6c757d;
            }
            .ck-content-view a {
              color: #0d6efd;
              text-decoration: none;
              cursor: pointer;
              pointer-events: auto;
            }
            .ck-content-view a:hover {
              text-decoration: underline;
              color: #0a58ca;
            }
            .ck-content-view a:visited {
              color: #6f42c1;
            }
            .ck-content-view strong {
              font-weight: 600;
            }
            .ck-content-view em {
              font-style: italic;
            }
          `}</style>
        </>
      )}
    </div>
  );
};

export default Active;

